import { useState } from 'react';
import { motion } from 'framer-motion';
import { FiLock } from 'react-icons/fi';
import { Button, Input, message } from 'antd';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { apiUrl, apiFetch } from '../api';
import logo from '../assets/logo.png';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const loggedInToken = localStorage.getItem('token');
  const isLoggedIn = !!loggedInToken;

  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessageText('');
    setError('');

    if (password !== confirmPassword) {
      setLoading(false);
      setError('Passwords do not match');
      message.error('Passwords do not match');
      return;
    }

    try {
      let response;
      if (isLoggedIn) {
        response = await apiFetch('/api/auth/change-password', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ currentPassword, newPassword: password }),
        });
      } else {
        response = await fetch(apiUrl('/api/auth/reset-password'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, password }),
        });
      }

      const data = await response.json();

      if (response.ok) {
        setMessageText(data.message || 'Password updated successfully');
        message.success(data.message || 'Password updated successfully');
        if (isLoggedIn) {
          setTimeout(() => navigate('/dashboard'), 1500);
        } else {
          setTimeout(() => navigate('/login'), 1500);
        }
      } else {
        setError(data.message || 'Unable to update password');
        message.error(data.message || 'Unable to update password');
      }
    } catch {
      setError('Connection error. Is the server running?');
      message.error('Connection error. Is the server running?');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[linear-gradient(180deg,#0b4d7b_0%,#067fc2_100%)] flex items-center justify-center px-4 py-10 font-sans">
      <div className="absolute inset-0 pointer-events-none opacity-70">
        <div className="absolute left-[-8%] top-[10%] h-64 w-64 rounded-full bg-white/10 blur-[90px]" />
        <div className="absolute right-[-10%] bottom-[8%] h-72 w-72 rounded-full bg-cyan-300/20 blur-[110px]" />
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative z-10 w-full max-w-[520px]">
        <div className="flex flex-col items-center gap-8">
          <div className="flex items-center justify-center gap-3">
            <img src={logo} alt="VMS Logo" className="h-20 w-auto rounded-[24px] object-contain bg-white/10 p-2 backdrop-blur-sm shadow-[0_16px_40px_rgba(0,0,0,0.18)]" />
            <div className="text-[54px] font-semibold leading-none tracking-[-0.05em] text-white">VMS</div>
          </div>

          <div className="wm-card w-full overflow-hidden rounded-[24px] border border-white/70 bg-white p-8 shadow-[0_22px_80px_rgba(2,24,49,0.24)] md:p-12">
            <div className="mb-8 text-center">
              <h1 className="text-[34px] font-semibold tracking-[-0.03em] text-slate-700">
                {isLoggedIn ? 'Change Password' : 'Reset Password'}
              </h1>
              <p className="mt-2 text-[18px] text-slate-700">
                {isLoggedIn ? 'Update your VMS account password.' : 'Choose a new password for your VMS account.'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {!isLoggedIn && !token && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm font-medium text-rose-700">
                  Missing reset token. Please open the link from your email again.
                </div>
              )}

              {messageText && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-medium text-emerald-700">
                  {messageText}
                </div>
              )}

              {error && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm font-medium text-rose-700">
                  {error}
                </div>
              )}

              {isLoggedIn && (
                <div className="space-y-2">
                  <label className="block text-[18px] font-medium text-slate-700">
                    <span className="text-rose-500">*</span> Current Password
                  </label>
                  <Input.Password
                    size="large"
                    prefix={<FiLock className="mr-2 text-sky-600" />}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    className="h-14 rounded-[10px] border-slate-200 text-[16px]"
                    required
                  />
                </div>
              )}

              <div className="space-y-2">
                <label className="block text-[18px] font-medium text-slate-700">
                  <span className="text-rose-500">*</span> New Password
                </label>
                <Input.Password
                  size="large"
                  prefix={<FiLock className="mr-2 text-sky-600" />}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter a new password"
                  className="h-14 rounded-[10px] border-slate-200 text-[16px]"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="block text-[18px] font-medium text-slate-700">
                  <span className="text-rose-500">*</span> Confirm Password
                </label>
                <Input.Password
                  size="large"
                  prefix={<FiLock className="mr-2 text-sky-600" />}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm the new password"
                  className="h-14 rounded-[10px] border-slate-200 text-[16px]"
                  required
                />
              </div>

              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                disabled={!isLoggedIn && !token}
                className="w-full h-14 rounded-[10px] border-0 bg-[linear-gradient(180deg,#0b5f95_0%,#0a4c7a_100%)] text-[16px] font-semibold shadow-none hover:!bg-[linear-gradient(180deg,#0a5384_0%,#083c61_100%)]"
              >
                {isLoggedIn ? 'CHANGE PASSWORD' : 'RESET PASSWORD'}
              </Button>

              <div className="pt-4 text-center text-[17px] text-slate-600">
                <Link to={isLoggedIn ? "/dashboard" : "/login"} className="font-medium text-sky-500 underline decoration-sky-400/40 underline-offset-4">
                  {isLoggedIn ? 'Back to Dashboard' : 'Back to Sign In'}
                </Link>
              </div>
            </form>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ResetPassword;